import { useAuth, useIsBrokerOnboardingNewFlowEnabled } from 'providers';
import { PermissionType } from 'enums/permissionType.enum';
import { UnauthorizedPage } from 'pages';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { NewSignupPageSectionEnum } from '../pages/SignupPage/enums';
import { useBrokerOnboarding, useGetUser } from 'queries';
import { PageURL } from './pageURL';
import { IProtectedRouteProps } from './types';

export const ProtectedRoute: React.FC<IProtectedRouteProps> = ({ children, authRoles }) => {
  const { data: user, isInitialLoading } = useGetUser();
  const { currentSignUpStep, isLoaded, isSignedIn } = useAuth();
  const isBrokerOnboardingNewFlowEnabled = useIsBrokerOnboardingNewFlowEnabled();
  const {
    data: brokerOnboarding,
    isFetched: isBrokerOnboardingFetched,
    isError: isBrokerOnboardingError,
  } = useBrokerOnboarding();
  const navigate = useNavigate();
  const isProtectedPage = authRoles.length > 0;
  const userGroups: PermissionType[] = user?.roles.map(r => r.role.type) ?? [];
  const isUserInAuthRole = authRoles.some((role: PermissionType) => userGroups.includes(role));

  useEffect(() => {
    if (!isLoaded || !isProtectedPage) {
      return;
    }

    if (!isSignedIn) {
      navigate(PageURL.LOGIN);
      return;
    }

    if (isBrokerOnboardingNewFlowEnabled) {
      if (!isBrokerOnboardingFetched) {
        return;
      }

      if (isBrokerOnboardingError) {
        return;
      }

      const targetSection = brokerOnboarding?.section ?? NewSignupPageSectionEnum.POST_SIGN_UP_PAGE;

      if (targetSection !== NewSignupPageSectionEnum.COMPLETED) {
        navigate(`${PageURL.SIGNUP}?section=${targetSection}`, { replace: true });
      }

      return;
    }

    if (currentSignUpStep && currentSignUpStep !== NewSignupPageSectionEnum.COMPLETED) {
      navigate(`${PageURL.SIGNUP}?section=${currentSignUpStep}`);
    }
  }, [
    brokerOnboarding?.section,
    currentSignUpStep,
    isBrokerOnboardingError,
    isBrokerOnboardingFetched,
    isBrokerOnboardingNewFlowEnabled,
    isLoaded,
    isProtectedPage,
    isSignedIn,
    navigate,
  ]);

  if (!isLoaded || isInitialLoading) {
    return;
  }

  if (isBrokerOnboardingNewFlowEnabled && isProtectedPage && isSignedIn && !isBrokerOnboardingFetched) {
    return;
  }

  if (!isProtectedPage || (isProtectedPage && isSignedIn && isUserInAuthRole)) return <>{children}</>;

  return <UnauthorizedPage />;
};
